import { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { VoiceSelectionGrid } from '@/components/VoiceSelectionGrid'
import { useUpdateCharacter } from '@/hooks/useCharacter'
import { useUserInfo } from '@/hooks/useUserInfo'
import type { Database } from '@/types/database'
import { VOICE_OPTIONS } from '@/lib/constants'

type Character = Database['eh']['Tables']['characters']['Row']

interface EditCharacterDialogProps {
  character: Character | null
  isOpen: boolean
  onClose: () => void
  onSuccess?: (newCharacter: Character) => void
  onUpgrade: () => void
  context?: string,
  initialSelectedVoice?: string
}

export function EditCharacterDialog({
  character,
  isOpen,
  onClose,
  onSuccess,
  onUpgrade,
  context,
  initialSelectedVoice,
}: EditCharacterDialogProps) {
  const [selectedVoice, setSelectedVoice] = useState(initialSelectedVoice || character?.voice || '')
  const { isSubscriber } = useUserInfo()
  const { mutate: updateCharacter, isPending } = useUpdateCharacter()

  const genderFilter = useCallback((opt: { mOnly?: boolean, fOnly?: boolean }) => {
      const gender = character?.gender ?? 'f'
      return (opt.fOnly && gender === 'f')
        || (opt.mOnly && gender === 'm')
        || (!opt.fOnly && !opt.mOnly);
    }, [character?.gender])

  useEffect(() => {
    if (!isOpen) {
      setSelectedVoice(initialSelectedVoice || character?.voice || '') // reset when closing
    }

    const defaultVoice = VOICE_OPTIONS.filter(genderFilter).find(opt => opt.isDefault)?.value || ''
    if (character) {
      // Set system default voice for display as fallback
      setSelectedVoice(initialSelectedVoice || character.voice || defaultVoice)
    }
  }, [character, genderFilter, isOpen, initialSelectedVoice])

  if (!character) {
    return null
  }

  const handleConfirm = () => {
    updateCharacter(
      { id: character.id, voice: selectedVoice },
      {
        onSuccess: (newCharacter) => {
          onSuccess?.(newCharacter)
          onClose()
        },
      },
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900">
        <DialogHeader>
          <DialogTitle>Change Voice</DialogTitle>
        </DialogHeader>
        {context && (
          <div className="px-6">
            <p className="text-sm text-zinc-300 leading-relaxed">{context}</p>
          </div>
        )}
        <div className="py-2">
          <VoiceSelectionGrid
            gender={character.gender as 'f' | 'm'}
            selectedVoice={selectedVoice}
            onSelectVoice={setSelectedVoice}
            isSubscriber={isSubscriber}
            onUpgrade={onUpgrade}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="pink" onClick={handleConfirm} disabled={isPending}>
            {isPending ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
} 